import { IconLayer, PolygonLayer } from '@deck.gl/layers';
import {
  SUBWAY_STATION_EXTENTS_VERSION,
  SUBWAY_STATION_EXTENTS_DATA_REVISION,
  type MultiPolygonCoordinates,
  type SubwayEntranceType,
  type SubwayStationEntrance,
  type SubwayStationExtent,
  type SubwayStationExtentsPayload,
} from '@vitals/shared';

export type ExtentGeometryLevel = 'neighborhood' | 'detail';

export function extentGeometryLevel(zoom: number): ExtentGeometryLevel | null {
  if (zoom < 14) return null;
  return zoom < 16 ? 'neighborhood' : 'detail';
}

function validPolygons(value: unknown): value is MultiPolygonCoordinates {
  return Array.isArray(value) && value.length > 0 && value.every((polygon) =>
    Array.isArray(polygon) && polygon.length > 0 && polygon.every((ring) =>
      Array.isArray(ring) && ring.length >= 4 && ring.every((position) =>
        Array.isArray(position) && position.length === 2 && position.every(Number.isFinite)
      )
      && ring[0]![0] === ring.at(-1)![0] && ring[0]![1] === ring.at(-1)![1]
      && new Set(ring.slice(0, -1).map(([lng, lat]) => `${lng},${lat}`)).size >= 3
    )
  );
}

function validExtent(value: unknown): value is SubwayStationExtent {
  if (typeof value !== 'object' || value === null) return false;
  const extent = value as Partial<SubwayStationExtent>;
  return typeof extent.shapeId === 'string' && typeof extent.stationId === 'string'
    && typeof extent.name === 'string' && validPolygons(extent.neighborhood)
    && validPolygons(extent.detail);
}

const ENTRANCE_TYPES = new Set<SubwayEntranceType>([
  'stair', 'elevator', 'escalator', 'station-house', 'ramp', 'other',
]);

function validEntrance(value: unknown): value is SubwayStationEntrance {
  if (typeof value !== 'object' || value === null) return false;
  const entrance = value as Partial<SubwayStationEntrance>;
  return typeof entrance.id === 'string' && typeof entrance.stationId === 'string'
    && typeof entrance.name === 'string' && ENTRANCE_TYPES.has(entrance.type as SubwayEntranceType)
    && Array.isArray(entrance.position) && entrance.position.length === 2
    && entrance.position.every(Number.isFinite)
    && typeof entrance.entryAllowed === 'boolean' && typeof entrance.exitAllowed === 'boolean';
}

export async function fetchStationExtents(baseUrl = ''): Promise<SubwayStationExtentsPayload | null> {
  try {
    const response = await fetch(
      `${baseUrl}/api/subway-station-extents?v=${SUBWAY_STATION_EXTENTS_VERSION}`
        + `&data=${SUBWAY_STATION_EXTENTS_DATA_REVISION}`,
    );
    if (!response.ok) return null;
    const value = await response.json() as Partial<SubwayStationExtentsPayload>;
    if (value.version !== SUBWAY_STATION_EXTENTS_VERSION) return null;
    if (value.dataRevision !== SUBWAY_STATION_EXTENTS_DATA_REVISION) return null;
    return {
      version: value.version,
      dataRevision: value.dataRevision,
      extents: Array.isArray(value.extents) ? value.extents.filter(validExtent) : [],
      entrances: Array.isArray(value.entrances) ? value.entrances.filter(validEntrance) : [],
    };
  } catch {
    return null;
  }
}

export class StationExtentSelection {
  private selected: string | null = null;
  get(): string | null { return this.selected; }
  select(stationId: string): void { this.selected = stationId; }
  clear(): void { this.selected = null; }
}

/** Keep physical extents below ribbons/markers and selected entrances above them. */
export function orderSubwayStaticLayers<
  D extends { id: string },
  L extends { id: string },
  S extends { id: string },
>(
  detail: D[],
  line: L,
  station: S,
): (D | L | S)[] {
  return [
    ...detail.filter((layer) => layer.id === 'subwayStationExtents'),
    line,
    station,
    ...detail.filter((layer) => layer.id === 'subwayStationEntrances'),
  ];
}

type Direction = 'entry-exit' | 'entry' | 'exit' | 'closed';
export function entranceGlyphKey(
  entrance: Pick<SubwayStationEntrance, 'type' | 'entryAllowed' | 'exitAllowed'>,
): string {
  const direction: Direction = entrance.entryAllowed && entrance.exitAllowed ? 'entry-exit'
    : entrance.entryAllowed ? 'entry' : entrance.exitAllowed ? 'exit' : 'closed';
  return `${entrance.type}-${direction}`;
}

const TYPES: SubwayEntranceType[] = ['stair', 'elevator', 'escalator', 'station-house', 'ramp', 'other'];
const DIRECTIONS: Direction[] = ['entry-exit', 'entry', 'exit', 'closed'];
const CELL = 24;
const glyph = (type: SubwayEntranceType, direction: Direction, x: number, y: number) => {
  const symbol = ({ stair: 'S', elevator: '↕', escalator: '↗', 'station-house': '⌂', ramp: '⌁', other: '•' })[type];
  const directionSymbol = ({ 'entry-exit': '↔', entry: '→', exit: '←', closed: '–' })[direction];
  return `<g transform="translate(${x} ${y})"><circle cx="12" cy="12" r="10" fill="#e8eefc" stroke="#080c14" stroke-width="2"/><text x="12" y="13" text-anchor="middle" font-family="sans-serif" font-size="9" font-weight="700" fill="#080c14">${symbol}</text><text x="12" y="20" text-anchor="middle" font-family="sans-serif" font-size="6" fill="#080c14">${directionSymbol}</text></g>`;
};
const iconEntries = TYPES.flatMap((type, row) => DIRECTIONS.map((direction, column) => ({
  key: `${type}-${direction}`, row, column, type, direction,
})));
const atlasSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="${CELL * DIRECTIONS.length}" height="${CELL * TYPES.length}">${iconEntries.map(({ type, direction, column, row }) => glyph(type, direction, column * CELL, row * CELL)).join('')}</svg>`;
const ENTRANCE_ATLAS = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(atlasSvg)}`;
const ENTRANCE_MAPPING = Object.fromEntries(iconEntries.map(({ key, row, column }) => [
  key, { x: column * CELL, y: row * CELL, width: CELL, height: CELL, anchorX: 12, anchorY: 12, mask: false },
]));

interface ExtentDatum {
  shapeId: string;
  stationId: string;
  name: string;
  polygon: MultiPolygonCoordinates[number];
}

export interface PreprocessedStationExtents {
  neighborhood: ExtentDatum[];
  detail: ExtentDatum[];
  entrancesByStation: Map<string, SubwayStationEntrance[]>;
}

const preprocessed = new WeakMap<SubwayStationExtentsPayload, PreprocessedStationExtents>();

export function preprocessStationExtents(
  payload: SubwayStationExtentsPayload,
): PreprocessedStationExtents {
  const cached = preprocessed.get(payload);
  if (cached) return cached;
  const flatten = (level: ExtentGeometryLevel): ExtentDatum[] => payload.extents.flatMap((extent) =>
    extent[level].map((polygon) => ({
      shapeId: extent.shapeId, stationId: extent.stationId, name: extent.name, polygon,
    }))
  );
  const entrancesByStation = new Map<string, SubwayStationEntrance[]>();
  for (const entrance of payload.entrances) {
    const stationEntrances = entrancesByStation.get(entrance.stationId) ?? [];
    stationEntrances.push(entrance);
    entrancesByStation.set(entrance.stationId, stationEntrances);
  }
  const result = {
    neighborhood: flatten('neighborhood'),
    detail: flatten('detail'),
    entrancesByStation,
  };
  preprocessed.set(payload, result);
  return result;
}

export function buildStationExtentLayers(
  payload: SubwayStationExtentsPayload,
  zoom: number,
  selectedStationId: string | null,
): (PolygonLayer<ExtentDatum> | IconLayer<SubwayStationEntrance>)[] {
  const level = extentGeometryLevel(zoom);
  const entranceLayer = (): IconLayer<SubwayStationEntrance> | null => {
    if (selectedStationId === null) return null;
    const entrancesData = preprocessStationExtents(payload).entrancesByStation.get(selectedStationId) ?? [];
    if (entrancesData.length === 0) return null;
    return new IconLayer<SubwayStationEntrance>({
      id: 'subwayStationEntrances',
      data: entrancesData,
      iconAtlas: ENTRANCE_ATLAS,
      iconMapping: ENTRANCE_MAPPING,
      getIcon: entranceGlyphKey,
      getPosition: (entrance) => entrance.position,
      getSize: 20,
      sizeUnits: 'pixels',
      sizeMinPixels: 16,
      sizeMaxPixels: 26,
      pickable: true,
    });
  };
  if (level === null) {
    const entrances = entranceLayer();
    return entrances ? [entrances] : [];
  }
  const data = preprocessStationExtents(payload)[level];
  const extents = new PolygonLayer<ExtentDatum>({
    id: 'subwayStationExtents',
    data,
    getPolygon: (datum) => datum.polygon,
    getFillColor: (datum) => selectedStationId === null
      ? [136, 164, 210, 38]
      : datum.stationId === selectedStationId ? [177, 207, 255, 95] : [100, 119, 150, 20],
    getLineColor: (datum) =>
      datum.stationId === selectedStationId ? [220, 235, 255, 210] : [146, 174, 220, zoom >= 16 ? 120 : 75],
    getLineWidth: (datum) => datum.stationId === selectedStationId ? 2 : zoom >= 16 ? 1.25 : 0.75,
    updateTriggers: {
      getFillColor: [selectedStationId],
      getLineColor: [selectedStationId, zoom >= 16],
      getLineWidth: [selectedStationId, zoom >= 16],
    },
    lineWidthUnits: 'pixels',
    filled: true,
    stroked: true,
    pickable: true,
  });
  const entrances = entranceLayer();
  return entrances ? [extents, entrances] : [extents];
}
